import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  loadPays,
  loadVilles,
  resolvePaysName,
  resolveVilleName,
  villesForPays,
  type PaysOption,
  type VilleOption,
} from '@/lib/geo';

function dedupeVilles(villes: VilleOption[]): VilleOption[] {
  const seen = new Set<string>();
  return villes
    .filter((v) => {
      const key = (v.nom_ville || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.nom_ville.localeCompare(b.nom_ville, 'fr'));
}

/**
 * Badge de localisation affiché en haut du chat IA.
 * Affiche la ville / le pays du profil et permet de les modifier à la volée,
 * sans quitter la conversation.
 */
export default function ChatLocationBadge() {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [paysList, setPaysList] = useState<PaysOption[]>([]);
  const [villesList, setVillesList] = useState<VilleOption[]>([]);
  const [pays, setPays] = useState('');
  const [ville, setVille] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = useMemo(() => {
    const v = (user?.Ville || '').toString().trim();
    const p = (user?.Pays || '').toString().trim();
    return [v, p].filter(Boolean).join(', ');
  }, [user?.Ville, user?.Pays]);

  // Chargement des référentiels à l'ouverture du panneau.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [paysData, villesData] = await Promise.all([loadPays(), loadVilles()]);
        if (cancelled) return;
        setPaysList(paysData);
        setVillesList(villesData);
        setPays(resolvePaysName(user.Pays, paysData));
        setVille(resolveVilleName(user.Ville, villesData));
      } catch {
        if (!cancelled) setError('Impossible de charger les pays et villes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  const villesDisponibles = useMemo(
    () => dedupeVilles(villesForPays(pays, paysList, villesList)),
    [pays, paysList, villesList],
  );

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from('users')
        .update({ Pays: pays, Ville: ville })
        .eq('id', user.id);
      if (dbError) throw dbError;
      updateUser({ Pays: pays, Ville: ville });
      setOpen(false);
    } catch {
      setError('La sauvegarde a échoué. Réessayez.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const selectClass =
    'w-full h-11 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Modifier ma ville / mon pays"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap max-w-[60%]"
      >
        <i className="ri-map-pin-2-line text-base leading-none text-accent-500 flex-shrink-0"></i>
        <span className="truncate">{label || 'Ajouter ma ville'}</span>
        <i className="ri-edit-line text-[11px] text-foreground-400 flex-shrink-0"></i>
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setOpen(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/60">
              <h3 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2">
                <i className="ri-map-pin-2-line text-accent-500"></i>
                Ma localisation
              </h3>
              <button
                type="button"
                onClick={() => !saving && setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Fermer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5">
              <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
                L&apos;assistant IA utilise votre ville pour vous répondre localement. Vous pouvez la modifier ici sans
                quitter le chat.
              </p>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1.5">
                        Pays
                      </label>
                      <select
                        value={pays}
                        onChange={(e) => { setPays(e.target.value); setVille(''); }}
                        className={selectClass}
                      >
                        <option value="">Sélectionnez un pays</option>
                        {paysList.map((p) => (
                          <option key={p.id} value={p.nom_pays}>{p.nom_pays}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1.5">
                        Ville
                      </label>
                      <select
                        value={ville}
                        onChange={(e) => setVille(e.target.value)}
                        disabled={!pays || villesDisponibles.length === 0}
                        className={selectClass}
                      >
                        <option value="">
                          {!pays ? "Choisissez d'abord un pays" : villesDisponibles.length === 0 ? 'Aucune ville disponible' : 'Sélectionnez une ville'}
                        </option>
                        {villesDisponibles.map((v) => (
                          <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg mt-4">
                      <i className="ri-error-warning-line"></i>
                      {error}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-5 pb-5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={saving}
                className="flex-1 h-11 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}